import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Upload, Download, Copy, ExternalLink, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function BulkPaymentLinksPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState('');
  const [activeBatch, setActiveBatch] = useState(null);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/bulk-payment-links');
      setBatches(r.data?.batches || []);
      setBlockedMessage('');
    } catch (err) {
      if (err.response?.status === 403) {
        setBlockedMessage(err.response?.data?.detail || 'Upgrade your package to use bulk payment links.');
        setBatches([]);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to load bulk payment links');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatches();
  }, []);

  const validRows = useMemo(() => (preview?.rows || []).filter((row) => row.is_valid), [preview]);
  const invalidRows = useMemo(() => (preview?.rows || []).filter((row) => !row.is_valid), [preview]);

  const uploadPreview = async () => {
    if (!file) {
      toast.error('Choose a CSV or Excel file first');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setPreviewLoading(true);
    try {
      const r = await api.post('/dashboard/bulk-payment-links/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      toast.success('Preview loaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview file');
    } finally {
      setPreviewLoading(false);
    }
  };

  const createLinks = async () => {
    if (!preview || !validRows.length) {
      toast.error('Preview a valid file first');
      return;
    }
    setCreating(true);
    try {
      const r = await api.post('/dashboard/bulk-payment-links', {
        rows: preview.rows,
        source_filename: preview.filename || file?.name || '',
      });
      setActiveBatch(r.data?.batch || null);
      setPreview(null);
      setFile(null);
      toast.success(r.data?.message || 'Bulk payment links created');
      await loadBatches();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.invalid_rows) {
        toast.error('Some rows are invalid. Fix the preview errors and try again.');
      } else {
        toast.error(detail?.message || detail || 'Failed to create bulk links');
      }
    } finally {
      setCreating(false);
    }
  };

  const downloadExport = async (batchId) => {
    try {
      const res = await api.get(`/dashboard/bulk-payment-links/${batchId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `jjuma-bulk-payment-links-${batchId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV export ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export links');
    }
  };

  const loadBatch = async (batchId) => {
    try {
      const r = await api.get(`/dashboard/bulk-payment-links/${batchId}`);
      setActiveBatch(r.data?.batch || null);
      toast.success('Batch loaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load batch');
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success('Copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (blockedMessage) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Bulk Payment Links</h1>
        <Card className="rounded-2xl border border-amber-200 bg-amber-50">
          <CardContent className="p-5 text-amber-900">
            {blockedMessage}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Bulk Payment Links</h1>
          <p className="text-slate-500 text-sm mt-0.5">Upload a CSV or Excel sheet and generate payment links in bulk.</p>
        </div>
        <Button variant="outline" onClick={loadBatches} className="gap-2">
          <RefreshCw size={16} /> Refresh
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.05fr]">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Upload File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>CSV or Excel file</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="mt-1"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-slate-500 mt-1">Columns: customer_name, phone, email, amount, currency, reason, due_date.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={uploadPreview} className="bg-slate-900 hover:bg-slate-800 text-white" disabled={previewLoading}>
                <Upload size={16} className="mr-1.5" /> {previewLoading ? 'Previewing...' : 'Preview File'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setFile(null); setPreview(null); }} disabled={previewLoading}>
                Clear
              </Button>
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={createLinks} disabled={creating || !preview || !validRows.length || invalidRows.length > 0}>
                <Send size={16} className="mr-1.5" /> {creating ? 'Creating...' : 'Create Links'}
              </Button>
            </div>

            {preview && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{preview.filename || file?.name || 'Preview'}</p>
                    <p className="text-xs text-slate-500">
                      {preview.valid_rows} valid • {preview.invalid_rows} invalid • {preview.row_count} total
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-emerald-100 text-emerald-800">{preview.valid_rows} valid</Badge>
                    <Badge className="bg-rose-100 text-rose-800">{preview.invalid_rows} invalid</Badge>
                  </div>
                </div>

                <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Due date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(preview.rows || []).map((row) => (
                        <TableRow key={`${row.row_number}-${row.customer_name}`}>
                          <TableCell>{row.row_number}</TableCell>
                          <TableCell>
                            <div className="font-medium text-slate-900">{row.customer_name}</div>
                            <div className="text-xs text-slate-500">{row.phone || row.email || '-'}</div>
                          </TableCell>
                          <TableCell>{row.currency} {Number(row.amount || 0).toLocaleString()}</TableCell>
                          <TableCell>{row.due_date ? new Date(row.due_date).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>
                            {row.is_valid ? (
                              <Badge className="bg-emerald-100 text-emerald-800">Valid</Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-800">{row.errors?.[0] || 'Invalid'}</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {invalidRows.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-900">Fix these rows before creating links</p>
                    {invalidRows.map((row) => (
                      <div key={row.row_number} className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                        Row {row.row_number}: {row.errors.join(', ')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {activeBatch && (
            <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Created Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => downloadExport(activeBatch.id)}>
                    <Download size={14} className="mr-1" /> Export CSV
                  </Button>
                </div>
                {(activeBatch.created_links || []).map((item) => (
                  <div key={item.id} className="rounded-2xl border border-slate-200 p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{item.customer_name}</p>
                        <p className="text-xs text-slate-500">{item.currency} {Number(item.amount || 0).toLocaleString()} • {item.reason}</p>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-800">Created</Badge>
                    </div>
                    <p className="text-xs text-slate-500 break-all">{item.payment_link}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => copyText(item.payment_link)}>
                        <Copy size={14} className="mr-1" /> Copy link
                      </Button>
                      <Button type="button" size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.open(item.whatsapp_url, '_blank', 'noopener,noreferrer')}>
                        <ExternalLink size={14} className="mr-1" /> WhatsApp
                      </Button>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                      <p className="text-xs font-medium text-slate-900 mb-1">WhatsApp / SMS message</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.whatsapp_message}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Previous Batches</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {batches.length === 0 ? (
                <p className="text-sm text-slate-500">No bulk uploads yet.</p>
              ) : batches.map((batch) => (
                <div key={batch.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{batch.source_filename || 'Bulk upload'}</p>
                      <p className="text-xs text-slate-500">
                        {batch.total_rows || 0} rows • {batch.created_link_count || batch.created_links?.length || 0} links • {batch.created_at ? new Date(batch.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                    <Badge className="bg-slate-100 text-slate-700">{batch.created_link_count || 0} links</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button type="button" size="sm" variant="outline" onClick={() => loadBatch(batch.id)}>
                      View messages
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => downloadExport(batch.id)}>
                      <Download size={14} className="mr-1" /> Export
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
